// Single source of truth for all business-identifying data.
// Every page title, meta description, footer, JSON-LD field, and contact reference
// must use these values — no hardcoded business data anywhere else in the codebase.

export const BUSINESS = {
  legalName: "M.A.D. Title Agency LLC",
  displayName: "M.A.D. Title Agency",
  shortName: "M.A.D. Title",

  address: {
    street: "515 Amboy Avenue",
    city: "Woodbridge",
    state: "NJ",
    stateFull: "New Jersey",
    zip: "07095",
    county: "Middlesex County",
    country: "US",
  },

  geo: {
    latitude: 40.5576,
    longitude: -74.2846,
  },

  contact: {
    phone: "+17328745425",
    phoneDisplay: "(732) 874-5425",
    email: "madtitleagency@gmail.com",
    fax: "",
  },

  hours: {
    monFri: "9:00 AM – 5:00 PM",
    sat: "By appointment",
    sun: "Closed",
  },

  domain: "madtitle.pro",
  siteUrl: "https://madtitle.pro",
  ogImage: "/og/madtitle-og.png",
  logo: "/madtitle.png",

  social: {
    facebook: "",
    linkedin: "",
    instagram: "",
  },
} as const;

export const NAV = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Services", href: "/services" },
  { label: "Resource Center", href: "/resources" },
  { label: "Contact", href: "/contact" },
  { label: "Locations", href: "/locations" },
] as const;

export const SERVICES = [
  {
    slug: "residential-closings",
    title: "Residential Closings",
    description: "Complete title services for home purchases and refinances across New Jersey.",
    icon: "home" as const,
  },
  {
    slug: "commercial-closings",
    title: "Commercial Closings",
    description: "Commercial closing coordination for investors, developers, and lenders across New Jersey.",
    icon: "building" as const,
  },
  {
    slug: "title-insurance",
    title: "Title Insurance",
    description: "Owner's and lender's title insurance policies protecting your real estate investment in New Jersey.",
    icon: "shield" as const,
  },
  {
    slug: "title-search",
    title: "Title Search & Examination",
    description: "Thorough title searches and examinations to surface liens, encumbrances, and ownership history.",
    icon: "search" as const,
  },
  {
    slug: "refinance",
    title: "Refinance Closings",
    description: "Refinance title services and closings coordinated with your lender — fast turnarounds in NJ.",
    icon: "refresh" as const,
  },
] as const;

// Metadata for each route, per v1.1 spec §2.1
export const PAGE_META: Record<string, { title: string; description: string }> = {
  "/": {
    title: `${BUSINESS.displayName} — Title & Closing Services in ${BUSINESS.address.city}, ${BUSINESS.address.state}`,
    description: `Trusted residential and commercial title and closing services in ${BUSINESS.address.city}, ${BUSINESS.address.state}. Fast, secure, reliable.`,
  },
  "/about": {
    title: `About ${BUSINESS.displayName} — ${BUSINESS.address.city}, ${BUSINESS.address.state}`,
    description: `Meet the team behind ${BUSINESS.displayName}. Local expertise, experienced staff, and a focus on clean closings.`,
  },
  "/services": {
    title: `Title & Closing Services — ${BUSINESS.displayName}`,
    description: `Residential closings, commercial closings, title insurance, title search, and refinance services in ${BUSINESS.address.state}.`,
  },
  "/services/residential-closings": {
    title: `Residential Closings in ${BUSINESS.address.city}, ${BUSINESS.address.state} — ${BUSINESS.displayName}`,
    description: `Smooth residential real estate closings for buyers, sellers, and agents across ${BUSINESS.address.county}, ${BUSINESS.address.state}.`,
  },
  "/services/title-insurance": {
    title: `Title Insurance in ${BUSINESS.address.state} — ${BUSINESS.displayName}`,
    description: `Owner's and lender's title insurance policies protecting your real estate investment in ${BUSINESS.address.stateFull}.`,
  },
  "/services/title-search": {
    title: `Title Search & Examination — ${BUSINESS.displayName}`,
    description: `Thorough title searches and examinations to surface liens, encumbrances, and ownership history.`,
  },
  "/services/commercial-closings": {
    title: `Commercial Real Estate Closings in ${BUSINESS.address.state} — ${BUSINESS.displayName}`,
    description: `Commercial closing coordination for investors, developers, and lenders across ${BUSINESS.address.stateFull}.`,
  },
  "/services/refinance": {
    title: `Refinance Closings in ${BUSINESS.address.state} — ${BUSINESS.displayName}`,
    description: `Refinance title services and closings coordinated with your lender — fast turnarounds in ${BUSINESS.address.state}.`,
  },
  "/contact": {
    title: `Contact ${BUSINESS.displayName} — ${BUSINESS.address.city}, ${BUSINESS.address.state}`,
    description: `Call or message ${BUSINESS.displayName} to start a closing, request a quote, or ask a title question.`,
  },
};
